import React, { useEffect, useMemo, useState } from 'react';
import {
  Objective,
  Review,
  ReviewType,
  ReviewCycle,
} from '../../types/pms';
import ReviewCycleSelector, { isHistoricalCycle } from './ReviewCycleSelector';
import { loadDirectReports } from '../../services/userLoader';
import { uniqueObjectivesForCycle } from '../../utils/pmsFilters';
import { canonicalizeEmail, sameIdentity } from '../../services/identity';
import './ManagerDashboard.css';
import './pms-premium.css';

/*
 * Identity comparison on this page goes through canonicalizeEmail / sameIdentity, not a
 * lowercased `===` — which is why there is no local normalizeEmail here any more.
 *
 * Every employee has two accounts since the creyentetech.com → creyenteinfotech.com move, so a
 * row written before it carries the old-domain address while the roster reports the new one.
 * Lowercasing does not bring those together. The effects here were all silent: a member whose
 * objectives were filed under the other domain showed 0 objectives and 0% average progress, and
 * their completed reviews were not found either — so they were reported as owing a mid-year or
 * year-end review they had already done. See services/identity.ts.
 */

interface TeamMemberSummary {
  employeeId: string;
  employeeName: string;
  objectiveCount: number;
  averageProgress: number;
  hasMidYearReview: boolean;
  hasEndYearReview: boolean;
  hasMidYearData: boolean;
  hasEndYearData: boolean;
}

interface ManagerDashboardProps {
  /** Current manager ID */
  managerId: string;
  /** All team objectives (direct reports) */
  teamObjectives: Objective[];
  /** Reviews conducted by this manager */
  teamReviews: Review[];
  /** Available review cycles */
  reviewCycles: ReviewCycle[];
  /** Currently selected review cycle ID */
  selectedCycleId: string;
  /** Callback when review cycle changes */
  onCycleChange: (cycleId: string) => void;
  /** Team members reporting to this manager */
  teamMembers?: { id: string; email: string; name: string }[];
  /** Currently selected team member ID (empty = all) */
  selectedMemberId?: string;
  /** Callback when team member filter changes */
  onMemberChange?: (memberId: string) => void;
  /** Navigate to employee's detailed objectives view (Req 17.3) */
  onTeamMemberClick: (employeeId: string) => void;
  /** Click a pending approval objective */
  onObjectiveClick?: (objective: Objective) => void;
}

/**
 * ManagerDashboard Component
 * Pending approvals, team performance overview, and review action counts.
 * Requirements: 17.1, 17.2, 17.3, 17.4
 */
const ManagerDashboard: React.FC<ManagerDashboardProps> = ({
  managerId,
  teamObjectives,
  teamReviews,
  reviewCycles,
  selectedCycleId,
  onCycleChange,
  teamMembers: initialTeamMembers = [],
  selectedMemberId = '',
  onMemberChange,
  onTeamMemberClick,
  onObjectiveClick,
}) => {
  const [searchText, setSearchText] = useState('');
  const [showAllMembers, setShowAllMembers] = useState(false);
  const [teamMembers, setTeamMembers] = useState<
    { id: string; email: string; name: string }[]
  >([]);

  useEffect(() => {
    if (initialTeamMembers.length > 0) {
      setTeamMembers(initialTeamMembers);
      return;
    }

    (async () => {
      try {
        const reports = await loadDirectReports(managerId);

        setTeamMembers(
          reports.map((r) => ({
            id: r.email,
            email: r.email,
            name: r.name,
          }))
        );
      } catch (err) {
        console.error('Failed to load team members', err);
        setTeamMembers([]);
      }
    })();
  }, [managerId, initialTeamMembers]);

  /*
   * The objectives this page is actually about: unique objectives in the SELECTED objective
   * cycle, for the team members in scope.
   *
   * `teamObjectives` is every objective the team has ever had. The route fetches it with
   * getEmployeeObjectives(email) — no cycle argument — so it spans every objective cycle the
   * team has ever been through, and the fetch does not even re-run when the cycle dropdown
   * changes. Rendering `teamObjectives.length` as "Total Objectives" therefore reported the
   * team's entire history under the heading of one cycle; that is where 23 came from.
   *
   * uniqueObjectivesForCycle applies the two things that were missing: the objective-cycle
   * match, and dedupe by objective id (the fetch fans out per member and per email-domain
   * alias, so one row can arrive several times).
   */
  const cycleObjectives = useMemo(
    () => uniqueObjectivesForCycle(teamObjectives, selectedCycleId),
    [teamObjectives, selectedCycleId]
  );

  // Filter objectives by selected team member
  const filteredObjectives = useMemo(() => {
    if (!selectedMemberId) return cycleObjectives;
    return cycleObjectives.filter(o => sameIdentity(o.employeeId, selectedMemberId));
  }, [cycleObjectives, selectedMemberId]);

  // Determine if viewing historical (non-active) cycle (Req 22.1)
  const isHistorical = useMemo(
    () => isHistoricalCycle(reviewCycles, selectedCycleId),
    [reviewCycles, selectedCycleId]
  );

  // Team member summaries (Req 17.2)
  const teamMemberSummaries = useMemo(() => {
    const map = new Map<string, Objective[]>();
    for (const obj of filteredObjectives) {
      const employeeId = canonicalizeEmail(obj.employeeId) || obj.employeeId;
      const list = map.get(employeeId) || [];
      list.push(obj);
      map.set(employeeId, list);
    }

    const summaries: TeamMemberSummary[] = [];
    const sourceMembers = teamMembers.length > 0
      ? teamMembers
      : Array.from(map.entries()).map(([employeeId, objs]) => ({
          id: employeeId,
          email: employeeId,
          name: objs[0]?.employeeName || employeeId,
        }));

    const visibleMembers = selectedMemberId
      ? sourceMembers.filter(member => sameIdentity(member.email, selectedMemberId))
      : sourceMembers;

    visibleMembers.forEach((member) => {
      const employeeId = canonicalizeEmail(member.email) || member.id;
      const objs = map.get(employeeId) || [];
      const avgProgress =
        objs.length > 0
          ? Math.round(objs.reduce((s, o) => s + o.progressPercentage, 0) / objs.length)
          : 0;
      // Find all reviews for this employee (across any cycle — cycle IDs may be child IDs)
      const empReviews = teamReviews.filter(r => sameIdentity(r.employeeId, employeeId));
      const midYearReviews = empReviews.filter(r => r.reviewType === ReviewType.MID_YEAR);
      const endYearReviews = empReviews.filter(r => r.reviewType === ReviewType.END_YEAR);
      // Only set review flags when reviews actually exist for this employee
      const hasMidYearReview = midYearReviews.length > 0 ? true : undefined;
      const hasEndYearReview = endYearReviews.length > 0 ? true : undefined;
      summaries.push({
        employeeId,
        employeeName: objs[0]?.employeeName || member.name || member.email,
        objectiveCount: objs.length,
        averageProgress: avgProgress,
        hasMidYearReview: hasMidYearReview ?? false,
        hasEndYearReview: hasEndYearReview ?? false,
        hasMidYearData: midYearReviews.length > 0,
        hasEndYearData: endYearReviews.length > 0,
      });
    });
    const q = searchText.trim().toLowerCase();
    if (!q) return summaries;
    return summaries.filter((member) => {
      const nameMatch = member.employeeName.toLowerCase().includes(q);
      const email = member.employeeId;
      const emailMatch = (email || '').toLowerCase().includes(q);
      return nameMatch || emailMatch;
    });
  }, [filteredObjectives, teamReviews, searchText, selectedMemberId, teamMembers]);

  // Counts (Req 17.4) — only count employees who have objectives and are missing reviews
  const midYearPending = teamMemberSummaries.filter((m) => m.objectiveCount > 0 && !m.hasMidYearData).length;
  const endYearPending = teamMemberSummaries.filter((m) => m.objectiveCount > 0 && !m.hasEndYearData).length;

  return (
    <div className="pms-manager-dashboard">
      {/* Header */}
      <div className="pms-manager-dashboard__header">
        <div className="pms-manager-dashboard__header-left">
          <h1 className="pms-manager-dashboard__title">Team Performance</h1>
        </div>
        <div className="pms-manager-dashboard__filters">
          <ReviewCycleSelector
            cycles={reviewCycles}
            selectedCycleId={selectedCycleId}
            onChange={onCycleChange}
          />
        </div>
      </div>

      {/* Summary Stat Cards — calculated from real data */}
      <div className="pms-mgr-stats">
        <div className="pms-mgr-stat-card">
          {/*
            filteredObjectives, not teamObjectives: cycle-scoped and deduped, and it follows the
            member filter like the rest of the page. The raw prop length ignored the cycle
            dropdown AND the member filter, so this number never changed whatever was selected.
          */}
          <strong className="pms-mgr-stat-card__value">{filteredObjectives.length}</strong>
          <span className="pms-mgr-stat-card__label">Total Objectives</span>
        </div>
        <div className="pms-mgr-stat-card pms-mgr-stat-card--warn">
          <strong className="pms-mgr-stat-card__value">{midYearPending}</strong>
          <span className="pms-mgr-stat-card__label">Mid-Year Pending</span>
        </div>
        <div className="pms-mgr-stat-card pms-mgr-stat-card--warn">
          <strong className="pms-mgr-stat-card__value">{endYearPending}</strong>
          <span className="pms-mgr-stat-card__label">End-Year Pending</span>
        </div>
        <div className="pms-mgr-stat-card">
          <strong className="pms-mgr-stat-card__value">{teamMembers.length}</strong>
          <span className="pms-mgr-stat-card__label">Team Members</span>
        </div>
      </div>

      {/* Historical cycle banner */}
      {isHistorical && (
        <div className="pms-manager-dashboard__historical-banner" role="status">
          📋 You are viewing a previous review cycle. All data is read-only.
        </div>
      )}

      {/* Team Section — search-first: show cards only after search */}
      <section className="pms-manager-dashboard__section" aria-label="Team Overview">
        <div className="pms-manager-dashboard__section-header">
          <h2 className="pms-manager-dashboard__section-title">
            Team Members
          </h2>
          <input
            className="pms-mgr-search"
            type="search"
            placeholder="Search employee by name or email..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoFocus
          />
        </div>

        {/* Empty state — shown when no search is active */}
        {!searchText.trim() && (
          <div className="pms-manager-dashboard__search-prompt">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" opacity="0.4">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <p className="pms-manager-dashboard__search-prompt-title">No employee selected</p>
            <p className="pms-manager-dashboard__search-prompt-text">Type an employee name or email to search team members and view objectives.</p>
          </div>
        )}

        {/* Search results — shown only when search text is entered */}
        {searchText.trim() && teamMemberSummaries.length === 0 && (
          <p className="pms-manager-dashboard__empty">No matching employees found.</p>
        )}

        {searchText.trim() && teamMemberSummaries.length > 0 && (
          <div className="pms-mgr-employee-grid">
            {(showAllMembers ? teamMemberSummaries : teamMemberSummaries.slice(0, 12)).map((member) => (
              <div
                key={member.employeeId}
                className="pms-mgr-employee-card"
                onClick={() => onTeamMemberClick(member.employeeId)}
              >
                <div className="pms-mgr-employee-card__avatar">
                  {(member.employeeName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="pms-mgr-employee-card__info">
                  <strong className="pms-mgr-employee-card__name">{member.employeeName}</strong>
                  <span className="pms-mgr-employee-card__email">{member.employeeId}</span>
                  <div className="pms-mgr-employee-card__meta">
                    <span>{member.objectiveCount} objective{member.objectiveCount !== 1 ? 's' : ''}</span>
                    <span className="pms-mgr-employee-card__sep">{'\u2022'}</span>
                    <span>{member.averageProgress}% avg progress</span>
                  </div>
                  <div className="pms-mgr-employee-card__progress-wrap">
                    <div className="pms-mgr-employee-card__progress-track">
                      <div className="pms-mgr-employee-card__progress-fill" style={{ width: `${member.averageProgress}%` }} />
                    </div>
                  </div>
                  <div className="pms-mgr-employee-card__badges">
                    {member.hasMidYearData && (
                      <span className={`pms-mgr-badge ${member.hasMidYearReview ? 'pms-mgr-badge--done' : 'pms-mgr-badge--pending'}`}>
                        Mid-Year: {member.hasMidYearReview ? 'Done' : 'Pending'}
                      </span>
                    )}
                    {member.hasEndYearData && (
                      <span className={`pms-mgr-badge ${member.hasEndYearReview ? 'pms-mgr-badge--done' : 'pms-mgr-badge--pending'}`}>
                        Year-End: {member.hasEndYearReview ? 'Done' : 'Pending'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="pms-mgr-employee-card__btn"
                  onClick={(e) => { e.stopPropagation(); onTeamMemberClick(member.employeeId); }}
                >
                  View Details {'\u2192'}
                </button>
              </div>
            ))}
          </div>
        )}

        {searchText.trim() && teamMemberSummaries.length > 12 && !showAllMembers && (
          <button className="pms-manager-dashboard__show-more" onClick={() => setShowAllMembers(true)}>
            Show {teamMemberSummaries.length - 12} more
          </button>
        )}
        {searchText.trim() && showAllMembers && teamMemberSummaries.length > 12 && (
          <button className="pms-manager-dashboard__show-more" onClick={() => setShowAllMembers(false)}>
            Show fewer
          </button>
        )}
      </section>
    </div>
  );
};

export default ManagerDashboard;
